import { requestService } from "src/services/requestService";
import Swal from "sweetalert2";

// El servidor embebe el payload como JSON string dentro del XML.
// Después de la conversión XML→JSON, wsResponse es ese string.
function parseWsResponse(response) {
  const raw = response?.wsResponse;
  if (!raw) return null;
  if (typeof raw === "string" && raw.trim() !== "") {
    try { return JSON.parse(atob(raw)); } catch { return null; }
  }
  return null;
}

function isOk(response) {
  return response?.status === "true";
}

// ── Plantillas ────────────────────────────────────────────────────────────────

export const ocrPlantillasListar = async () => {
  const response = await requestService({
    services: "ocrPlantillasListar",
    structure: {},
    asincrono: true,
  });
  if (!isOk(response)) return [];
  const data = parseWsResponse(response);
  return Array.isArray(data) ? data : data ? [data] : [];
};

export const ocrPlantillasCrear = async (params) => {
  return await requestService({ services: "ocrPlantillasCrear", structure: params });
};

export const ocrPlantillasActualizar = async (params) => {
  return await requestService({ services: "ocrPlantillasActualizar", structure: params });
};

export const ocrPlantillasEliminar = async (id) => {
  let confirmado = false;
  await Swal.fire({
    title: "¿Eliminar plantilla?",
    text: "Esta acción desactivará la plantilla.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
  }).then(async (result) => {
    if (result.isConfirmed) {
      const response = await requestService({
        services: "ocrPlantillasEliminar",
        structure: { id },
      });
      confirmado = isOk(response);
      if (confirmado) {
        await Swal.fire("Eliminada", "La plantilla fue desactivada.", "success");
      } else {
        await Swal.fire("Error", response?.message ?? "No se pudo eliminar.", "error");
      }
    }
  });
  return confirmado;
};

export const ocrPlantillasGuardarCampos = async (id_plantilla, campos) => {
  return await requestService({
    services: "ocrPlantillasGuardarCampos",
    structure: { id_plantilla, campos },
  });
};

export const ocrPlantillasGuardarRegiones = async (id_plantilla, regiones, imagen_referencia) => {
  return await requestService({
    services: "ocrPlantillasGuardarRegiones",
    structure: { id_plantilla, regiones, imagen_referencia },
  });
};

// ── Lotes ─────────────────────────────────────────────────────────────────────

export const ocrLotesListar = async () => {
  const response = await requestService({
    services: "ocrLotesListar",
    structure: {},
    asincrono: true,
  });
  if (!isOk(response)) return [];
  const data = parseWsResponse(response);
  return Array.isArray(data) ? data : data ? [data] : [];
};

export const ocrLotesCrear = async (params) => {
  return await requestService({ services: "ocrLotesCrear", structure: params });
};

export const ocrLotesRegistrarDoc = async (params) => {
  return await requestService({
    services: "ocrLotesRegistrarDoc",
    structure: params,
    asincrono: true,
  });
};

export const ocrLotesProcesar = async (id_lote) => {
  return await requestService({
    services: "ocrLotesProcesar",
    structure: { id_lote },
    asincrono: true,
  });
};

export const ocrLotesEstado = async (id) => {
  const response = await requestService({
    services: "ocrLotesEstado",
    structure: { id },
    asincrono: true,
  });
  if (!isOk(response)) return null;
  return parseWsResponse(response);
};

// ── Resultados ────────────────────────────────────────────────────────────────

export const ocrResultadosPorLote = async (id_lote) => {
  const response = await requestService({
    services: "ocrResultadosPorLote",
    structure: { id_lote },
    asincrono: true,
  });
  if (!isOk(response)) return [];
  const data = parseWsResponse(response);
  return Array.isArray(data) ? data : data ? [data] : [];
};

export const ocrResultadosPorDocumento = async (id_documento) => {
  const response = await requestService({
    services: "ocrResultadosPorDocumento",
    structure: { id_documento },
    asincrono: true,
  });
  if (!isOk(response)) return [];
  const data = parseWsResponse(response);
  // Devuelve { documento, campos } — retornamos los campos directamente
  if (data?.campos) return data.campos;
  return Array.isArray(data) ? data : data ? [data] : [];
};

export const ocrResultadosCorregir = async (id, valor_corregido) => {
  return await requestService({
    services: "ocrResultadosCorregir",
    structure: { id, valor_corregido },
  });
};

export const ocrResultadosRevisarTodos = async (id_documento) => {
  return await requestService({
    services: "ocrResultadosRevisarTodos",
    structure: { id_documento },
    asincrono: true,
  });
};

export const ocrResultadosReprocesar = async (id_documento) => {
  return await requestService({
    services: "ocrResultadosReprocesar",
    structure: { id_documento },
    asincrono: true,
  });
};

export const ocrAnalizarImagenes = async (params) => {
  return await requestService({
    services: "ocrLotesAnalizarImagen",
    structure: params,
  });
};

export const ocrAnalizarPorRegiones = async (params) => {
  return await requestService({
    services: "ocrLotesAnalizarPorRegiones",
    structure: {
      ...params,
      regiones: btoa(JSON.stringify(params.regiones)),
    },
  });
};

// ── Metadatos ─────────────────────────────────────────────────────────────────

export const ocrMetadatosListar = async (idTipoContenidos = null) => {
  const structure = idTipoContenidos ? { id_tipo_contenidos: idTipoContenidos } : {};
  const response = await requestService({
    services: "ocrMetadatosListar",
    structure,
    asincrono: true,
  });
  if (!isOk(response)) return [];
  const data = parseWsResponse(response);
  return Array.isArray(data) ? data : data ? [data] : [];
};

// ── Cuentas vinculadas a plantillas ──────────────────────────────────────────

export const ocrPlantillasCuentas = async () => {
  const response = await requestService({
    services: "ocrPlantillasCuentas",
    structure: {},
    asincrono: true,
  });
  if (!isOk(response)) return [];
  const data = parseWsResponse(response);
  return Array.isArray(data) ? data : data ? [data] : [];
};

// ── Estadísticas de análisis ──────────────────────────────────────────────────

export const ocrAnalisisEstadisticas = async (params = {}) => {
  const response = await requestService({
    services: "ocrAnalisisEstadisticas",
    structure: params,
    asincrono: true,
  });
  if (!isOk(response)) return null;
  return parseWsResponse(response);
};
